// ── Stage 1 Domain Classifier ─────────────────────────────────────────────────
// Cheap LLM call that maps a free-text user query to one or more domains
// before the Stage 2 decomposer runs.
//
// Why a separate call?
//   The Stage 2 decomposer prompt is scoped to only the registry sections
//   for models relevant to the user's domain. Without prior classification
//   we fell back to keyword lists, which miss ambiguous queries like
//   "body armor" or "enzyme inhibitor". A small focused LLM call is far
//   more robust than static keywords for an open-ended materials domain.
//
// Flow:
//   1. User types a query (no category chip selected).
//   2. classifyDomains(input) sends a <300-token prompt to the LLM.
//   3. LLM returns { categories: string[], domains: string[] }.
//   4. Caller uses the domains to scope the Stage 2 prompt.
//   5. On failure, caller falls back to keyword-based resolveSpec().

import { askLLM } from '../askLLM';
import { getFilteredCategories, buildCategoryPromptLines } from '../registry/categoryRegistry';

function buildPrompt(allowedCategories) {
  const cats = getFilteredCategories(allowedCategories);
  const knownDomains = cats.flatMap(c => c.domains);
  const defaultCat = cats[0]?.id || 'structural_materials';
  const defaultDomain = knownDomains[0] || 'inorganic_crystal';

  return `You are a domain classifier for a materials and chemistry discovery engine.
Given a user query, identify which research domains it belongs to.

Available categories (id: label — description → domains):
${buildCategoryPromptLines(allowedCategories)}

Rules:
1. Output ONLY a valid JSON object — no text outside JSON.
2. SINGLE DOMAIN ONLY unless the query EXPLICITLY asks for multiple material types (e.g. "ceramic-polymer composite"). Most queries belong to exactly ONE category and ONE domain.
3. "inorganic crystal", "crystal", "alloy", "ceramic", "metal", "oxide", "perovskite", "superconductor", "magnetic" → inorganic_crystal ONLY. NEVER add small_molecule or polymer to a crystal query.
4. "molecule", "drug", "ligand", "peptide", "small molecule" → small_molecule ONLY. NEVER add inorganic_crystal.
5. "polymer", "fiber", "resin", "elastomer" → polymer ONLY. NEVER add inorganic_crystal or small_molecule.
6. Multiple domains ONLY when the query explicitly says "composite", "hybrid", "multi-material", or names two different material types (e.g. "ceramic and polymer").
7. "domains" must be the union of domains from the matched categories.
8. If truly uncertain, default to {"categories":["${defaultCat}"],"domains":["${defaultDomain}"]}.

Schema: {"categories":["<id>",...],"domains":["<domain>",...],"confidence":"high|medium|low"}`;
}

/**
 * Stage 1 domain classifier.
 *
 * @param {string} userInput - Raw user query text
 * @param {AbortSignal} [signal] - Optional abort signal
 * @param {string[]|null} [allowedCategories] - Org-scoped category IDs, or null for all
 * @returns {Promise<{categories: string[], domains: string[], confidence: string}|null>}
 *   Returns the full classifier result object (categories, domains, confidence)
 *   or null on error so the caller can fall back to keyword inference.
 */
export async function classifyDomains(userInput, signal, allowedCategories) {
  const cats = getFilteredCategories(allowedCategories);
  const KNOWN_DOMAINS = new Set(cats.flatMap(c => c.domains));

  try {
    const text = await askLLM({
      systemInstruction: buildPrompt(allowedCategories),
      userText: `Classify this query: "${userInput}"`,
      signal,
      response_format: { type: 'json_object' },
      // Use a fast/cheap model for this lightweight call if the proxy supports it
      max_tokens: 256,
    });

    // Parse defensively
    let raw = text || '';
    // Strip markdown fences if present
    raw = raw.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '').trim();
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;

    const parsed = JSON.parse(match[0]);
    const domains = Array.isArray(parsed.domains) ? parsed.domains : [];
    const categories = Array.isArray(parsed.categories) ? parsed.categories : [];
    const confidence = parsed.confidence || 'medium';

    // Filter to only known domain strings to guard against hallucinations
    const valid = domains.filter(d => KNOWN_DOMAINS.has(d));
    return valid.length > 0 ? { categories, domains: valid, confidence } : null;
  } catch (_) {
    // Any failure → caller falls back to keyword inference
    return null;
  }
}
